import { Controller } from "@hotwired/stimulus"

/**
 * BikeHunterTabsController
 * 
 * Handles instant tab switching between Cronograma and Ruta views
 * with lazy-loaded Google Maps integration and data caching.
 */
export default class extends Controller {
  static targets = ["tab", "cronogramaPanel", "rutaPanel", "advertenciasPanel", "mapContainer", "loadingIndicator", "routeInfo", "warningsList", "warningCountBadge"]
  static values = {
    date: String,
    mapLoaded: { type: Boolean, default: false },
    activeTab: { type: String, default: "cronograma" }
  }

  connect() {
    console.log("🚴 BikeHunterTabs: Connected for date", this.dateValue)
    this.cachedRouteData = null
    this.map = null
    this.markers = []
    this.routePath = null
    this.mechanicRoutes = new Map() // userId -> {polyline, markers, originMarker, visible, ...info}
    this.pendingTicketId = null // Track ticket to return to from resolution
    this.currentInfoWindow = null // Track currently open InfoWindow

    // Listen for address updates to refresh route
    this.handleAddressUpdate = this.handleAddressUpdate.bind(this)
    window.addEventListener('address-updated', this.handleAddressUpdate)

    // Listen for geocode completion (from GeocodeJob via Turbo Stream)
    this.handleGeocodeCompleted = this.handleGeocodeCompleted.bind(this)
    window.addEventListener('geocode-completed', this.handleGeocodeCompleted)

    // Listen for en_ruta toggle changes
    this.handleEnRutaChanged = this.handleEnRutaChanged.bind(this)
    window.addEventListener('en-ruta-changed', this.handleEnRutaChanged)

    // Listen for edit ticket address events
    this.handleEditTicketAddress = this.handleEditTicketAddress.bind(this)
    window.addEventListener('edit-ticket-address', this.handleEditTicketAddress)

    // Listen for view/resolve ticket from map events
    this.handleViewTicketFromMap = this.handleViewTicketFromMap.bind(this)
    this.handleResolveTicketFromMap = this.handleResolveTicketFromMap.bind(this)
    this.handleToggleEnRutaFromMap = this.handleToggleEnRutaFromMap.bind(this)
    window.addEventListener('view-ticket-from-map', this.handleViewTicketFromMap)
    window.addEventListener('resolve-ticket-from-map', this.handleResolveTicketFromMap)
    window.addEventListener('toggle-en-ruta-from-map', this.handleToggleEnRutaFromMap)

    // Listen for resolution complete/cancel to return to map
    this.handleResolutionComplete = this.handleResolutionComplete.bind(this)
    window.addEventListener('ticket-resolution-complete', this.handleResolutionComplete)
    window.addEventListener('ticket-resolution-cancel', this.handleResolutionComplete)

    // Restore tab from sessionStorage if available
    const savedTab = sessionStorage.getItem(`bikeHunterTab_${this.dateValue}`)
    if (savedTab && savedTab !== this.activeTabValue) {
      this.activeTabValue = savedTab
      this.updateTabStyles()
      this.updatePanelVisibility()

      if (savedTab === "ruta") {
        this.loadMap()
      }
    }

    // Always check for warnings in background
    this.checkForWarnings()
  }

  disconnect() {
    console.log("🚴 BikeHunterTabs: Disconnected")
    window.removeEventListener('address-updated', this.handleAddressUpdate)
    window.removeEventListener('geocode-completed', this.handleGeocodeCompleted)
    window.removeEventListener('en-ruta-changed', this.handleEnRutaChanged)
    window.removeEventListener('edit-ticket-address', this.handleEditTicketAddress)
    window.removeEventListener('view-ticket-from-map', this.handleViewTicketFromMap)
    window.removeEventListener('resolve-ticket-from-map', this.handleResolveTicketFromMap)
    window.removeEventListener('toggle-en-ruta-from-map', this.handleToggleEnRutaFromMap)
    window.removeEventListener('ticket-resolution-complete', this.handleResolutionComplete)
    window.removeEventListener('ticket-resolution-cancel', this.handleResolutionComplete)
    this.markers = []
    this.map = null
    this.routePath = null
    // Clear multi-mechanic route data
    this.mechanicRoutes.forEach(rs => {
      if (rs.polyline) rs.polyline.setMap(null)
      rs.markers.forEach(m => m.setMap(null))
      if (rs.originMarker) rs.originMarker.setMap(null)
    })
    this.mechanicRoutes.clear()
    
    // Restore body scroll when disconnecting
    document.body.classList.remove("overflow-hidden")
  }

  /**
   * Handle address update events - refresh route
   */
  handleAddressUpdate(event) {
    console.log("🚴 BikeHunterTabs: Address updated, refreshing route...", event.detail)
    this.refreshRouteData()
    // Always check for warnings after an update, regardless of current tab
    this.checkForWarnings()
  }

  /**
   * Handle geocode completion from GeocodeJob (via Turbo Stream)
   * This is triggered when the background job finishes geocoding an address
   */
  handleGeocodeCompleted(event) {
    const { ticketId, status, latitude, longitude, error } = event.detail
    console.log(`📍 BikeHunterTabs: Geocode completed for ticket ${ticketId}:`, status)

    if (status === 'success') {
      // Geocoding successful - force refresh route to include the new ticket
      console.log(`📍 Ticket ${ticketId} now has coordinates (${latitude}, ${longitude}), recalculating route...`)
      this.cachedRouteData = null // Force cache invalidation
      
      // If we're on the ruta tab, reload the map immediately
      if (this.activeTabValue === 'ruta') {
        this.loadMap()
      }
      
      // Always refresh warnings
      this.checkForWarnings()
    } else {
      // Geocoding failed - just refresh warnings to show the error
      console.log(`📍 Ticket ${ticketId} geocoding failed: ${error}`)
      this.checkForWarnings()
    }
  }

  /**
   * Handle en_ruta toggle changes - refresh route to include/exclude ticket
   */
  handleEnRutaChanged(event) {
    const { ticketId, enRuta } = event.detail
    console.log(`🚴 BikeHunterTabs: Ticket ${ticketId} en_ruta changed to ${enRuta}`)
    
    // Invalidate cache and force map reload
    this.cachedRouteData = null
    this.mapLoadedValue = false // Force map to reload on next tab switch
    
    // If on ruta or advertencias tab, reload map immediately with fresh data
    if (this.activeTabValue === 'ruta' || this.activeTabValue === 'advertencias') {
      // Add small delay to let backend process the route optimization
      setTimeout(() => {
        console.log(`🚴 BikeHunterTabs: Refreshing map after en_ruta change (${enRuta ? 'added' : 'removed'} from route)`)
        this.loadMap()
      }, 500)
    }
  }

  /**
   * Handle edit ticket address/field request
   */
  handleEditTicketAddress(event) {
    const ticketId = event.detail.ticketId
    const field = event.detail.field || 'direccion_reparacion'
    console.log('🚨 [Warning Correction] Edit ticket address triggered:', {
      ticketId,
      field,
      eventDetail: event.detail,
      eventTarget: event.target,
      eventCurrentTarget: event.currentTarget
    })

    // Extra debug: check if ticket element exists before switching
    const ticketElement = document.getElementById(`ticket_${ticketId}`)
    console.log('🚨 [Warning Correction] Pre-switch: ticket element lookup:', {
      ticketId,
      found: !!ticketElement,
      ticketElement
    })

    this.switchToCronogramaAndZoomTicket(ticketId, { focusField: field })
  }

  /**
   * Handle view ticket from map - switch to Cronograma and zoom ticket
   */
  handleViewTicketFromMap(event) {
    const ticketId = event.detail.ticketId
    console.log("🚴 BikeHunterTabs: View ticket from map:", ticketId)

    this.switchToCronogramaAndZoomTicket(ticketId)
  }

  /**
   * Handle resolve ticket from map - switch to Cronograma, zoom ticket and open resolution form
   */
  handleResolveTicketFromMap(event) {
    const { ticketId, fechaProgramada } = event.detail
    console.log("🚴 [DEBUG] handleResolveTicketFromMap called:", {
      ticketId,
      fechaProgramada,
      eventDetail: event.detail,
      currentDate: this.dateValue,
      activeTab: this.activeTabValue
    })

    // Mark that user came from map - to return after resolution
    this.cameFromMap = true
    this.pendingTicketId = ticketId

    this.switchToCronogramaAndZoomTicket(ticketId, { openResolution: true, fechaProgramada })
  }

  /**
   * Handle toggle en ruta from map - toggle ticket without leaving map
   */
  handleToggleEnRutaFromMap(event) {
    const ticketId = event.detail.ticketId
    console.log("🚴 BikeHunterTabs: Toggle en ruta from map:", ticketId)

    // Find the ticket row in the Cronograma tab
    const ticketElement = document.getElementById(`apps_home_repairs_ticket_${ticketId}`)
    if (!ticketElement) {
      console.log("🚴 BikeHunterTabs: Ticket element not found:", ticketId)
      return
    }

    // Find the toggle button within the ticket element
    const toggleButton = ticketElement.querySelector('[data-action="click->en-ruta-toggle#toggle"]')
    if (!toggleButton) {
      console.log("🚴 BikeHunterTabs: Toggle button not found for ticket:", ticketId)
      return
    }

    // Check current state before toggling
    const wasEnRuta = toggleButton.getAttribute('aria-checked') === 'true'

    // Find the toggle in the InfoWindow if it's open
    const infoWindowToggle = document.querySelector(`.gm-infowindow-toggle[data-ticket-id="${ticketId}"]`)

    // Click the toggle button programmatically
    toggleButton.click()

    // Update the InfoWindow toggle visually after a short delay (to let the action process)
    setTimeout(() => {
      if (infoWindowToggle) {
        const isNowOn = toggleButton.getAttribute('aria-checked') === 'true'
        this.updateInfoWindowToggle(infoWindowToggle, isNowOn)
      }
      
      // If we're removing from route, close the InfoWindow since the marker will disappear
      if (wasEnRuta) {
        if (this.currentInfoWindow) {
          this.currentInfoWindow.close()
          this.currentInfoWindow = null
        }
      }
    }, 100)
  }

  /**
   * Update the visual state of an InfoWindow toggle button
   */
  updateInfoWindowToggle(toggleButton, isOn) {
    toggleButton.setAttribute('aria-checked', isOn)
    
    if (isOn) {
      toggleButton.classList.remove('bg-gray-200')
      toggleButton.classList.add('bg-green-500')
    } else {
      toggleButton.classList.remove('bg-green-500')
      toggleButton.classList.add('bg-gray-200')
    }

    // Update the indicator position
    const indicator = toggleButton.querySelector('.gm-infowindow-toggle-indicator')
    if (indicator) {
      if (isOn) {
        indicator.style.transform = 'translateX(24px)'
      } else {
        indicator.style.transform = 'translateX(0)'
      }
    }
  }

  /**
   * Handle resolution complete/cancel - return to map only if user came from map
   */
  handleResolutionComplete(event) {
    console.log("🚴 BikeHunterTabs: Resolution complete/cancel")

    // Only return to map if user came from there
    if (!this.cameFromMap) {
      console.log("🚴 BikeHunterTabs: User didn't come from map, staying in Cronograma")
      return
    }

    console.log("🚴 BikeHunterTabs: Returning to map")

    // Clear flags
    this.cameFromMap = false
    this.pendingTicketId = null

    // Switch back to Ruta tab
    this.activeTabValue = "ruta"
    sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, "ruta")
    this.updateTabStyles()
    this.updatePanelVisibility()

    // Refresh route data in case ticket was resolved
    this.refreshRouteData()
  }

  /**
   * Switch to Cronograma tab and zoom a specific ticket
   */
  switchToCronogramaAndZoomTicket(ticketId, options = {}) {
    const { openResolution = false, focusField = null, fechaProgramada } = options

    console.log(`🚴 Switching to Cronograma and zooming ticket`, {
      ticketId,
      openResolution,
      focusField,
      fechaProgramada,
      currentDate: this.dateValue
    })

    // Only switch to Cronograma if ticket is in today's column (first column with tabs)
    // If ticket is in another day column, it won't be in the Cronograma panel
    const isTicketInToday = !fechaProgramada || fechaProgramada === this.dateValue
    
    if (isTicketInToday) {
      // Switch to Cronograma for tickets in today's column
      this.activeTabValue = "cronograma"
      sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, "cronograma")
      this.updateTabStyles()
      this.updatePanelVisibility()

      // Wait for panel to be visible (remove hidden class triggers reflow)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log(`🚴 [DEBUG] Panel visibility updated, proceeding with ticket lookup`)
          
          // Short delay for DOM settlement
          setTimeout(() => {
            this.findAndZoomTicket(ticketId, { openResolution, focusField })
          }, 500)
        })
      })
    } else {
      // Ticket is in another day column - scroll to that column and find ticket
      console.log(`🚴 [DEBUG] Ticket is in another day column (${fechaProgramada}), scrolling there`)
      this.scrollToDateColumn(fechaProgramada)
      
      // Delay for scroll animation to complete
      setTimeout(() => {
        this.findAndZoomTicket(ticketId, { openResolution, focusField })
      }, 800)
    }
  }

  /**
   * Find and zoom a ticket element, with retry logic
   */
  findAndZoomTicket(ticketId, options = {}) {
    const { openResolution = false, focusField = null } = options
    
    console.log(`🚴 [DEBUG] Looking for ticket element`)
    // Use the correct DOM ID format: apps_home_repairs_ticket_${id}
    const ticketElement = document.getElementById(`apps_home_repairs_ticket_${ticketId}`)
    console.log(`🚴 [DEBUG] Ticket element lookup result:`, {
      ticketId,
      domId: `apps_home_repairs_ticket_${ticketId}`,
      found: !!ticketElement,
      ticketElement: ticketElement ? ticketElement.outerHTML.substring(0, 200) + '...' : null,
      allTickets: Array.from(document.querySelectorAll('[id^="apps_home_repairs_ticket_"]')).map(el => el.id)
    })
    
    if (ticketElement) {
      // Scroll to ticket
      console.log(`🚴 [DEBUG] Scrolling to ticket element`)
      ticketElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Get the card-zoom controller and toggle zoom
      const cardZoomController = this.application.getControllerForElementAndIdentifier(ticketElement, 'card-zoom')
      console.log(`🚴 [DEBUG] Card zoom controller lookup:`, {
        found: !!cardZoomController,
        controller: cardZoomController
      })
      
      if (cardZoomController) {
        // Ensure it's zoomed - simulate a click event with the correct currentTarget
        const isAlreadyZoomed = ticketElement.classList.contains('zoomed')
        console.log(`🚴 [DEBUG] Zooming ticket:`, {
          ticketId,
          isAlreadyZoomed,
          willZoom: !isAlreadyZoomed
        })
        
        if (!isAlreadyZoomed) {
          // Create a mock event that looks like a real click event
          const mockEvent = {
            currentTarget: ticketElement,
            target: ticketElement,
            stopPropagation: () => { },
            preventDefault: () => { }
          }
          cardZoomController.toggle(mockEvent)
          console.log(`🚴 [DEBUG] Zoom toggle executed`)
        }

        // If opening resolution, dispatch event to open resolution form
        if (openResolution) {
          console.log(`🚴 [DEBUG] Dispatching resolve-ticket event`)
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('resolve-ticket', { 
              detail: { ticketId: ticketId } 
            }))
          }, 1000)
        }

        // If focusing a field
        if (focusField) {
          setTimeout(() => {
            const input = ticketElement.querySelector(`[data-field="${focusField}"]`)
            console.log(`🚴 [DEBUG] Field focus lookup:`, {
              focusField,
              found: !!input,
              input
            })
            if (input) {
              input.focus()
              // Highlight the input temporarily
              input.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50')
              setTimeout(() => {
                input.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50')
              }, 2000)
            }
          }, 1000)
        }
      } else {
        console.error(`🚴 [DEBUG] Card zoom controller not found for ticket ${ticketId}`)
      }
    } else {
      console.error(`🚴 [DEBUG] Ticket element not found in DOM:`, {
        ticketId,
        allTicketElements: Array.from(document.querySelectorAll('[id^="ticket_"]')).map(el => el.id)
      })
      
      // Retry once more after additional delay if element not found
      setTimeout(() => {
        console.log(`🚴 [DEBUG] Retrying ticket lookup after additional delay`)
        const retryTicketElement = document.getElementById(`ticket_${ticketId}`)
        
        if (retryTicketElement) {
          console.log(`🚴 [DEBUG] Ticket found on retry!`)
          // Scroll to ticket
          retryTicketElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

          // Get the card-zoom controller and toggle zoom
          const cardZoomController = this.application.getControllerForElementAndIdentifier(retryTicketElement, 'card-zoom')
          
          if (cardZoomController && !retryTicketElement.classList.contains('zoomed')) {
            const mockEvent = {
              currentTarget: retryTicketElement,
              target: retryTicketElement,
              stopPropagation: () => { },
              preventDefault: () => { }
            }
            cardZoomController.toggle(mockEvent)
          }

          // If opening resolution
          if (openResolution) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('resolve-ticket', { 
                detail: { ticketId: ticketId } 
              }))
            }, 500)
          }

          // If focusing a field
          if (focusField) {
            setTimeout(() => {
              const input = retryTicketElement.querySelector(`[data-field="${focusField}"]`)
              if (input) {
                input.focus()
                input.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50')
                setTimeout(() => {
                  input.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50')
                }, 2000)
              }
            }, 500)
          }
        } else {
          console.error(`🚴 [DEBUG] Ticket still not found after retry:`, {
            ticketId,
            allTicketElements: Array.from(document.querySelectorAll('[id^="ticket_"]')).map(el => el.id)
          })
        }
      }, 1500)
    }
  }

  /**
   * Scroll to the date column for the given date
   */
  scrollToDateColumn(targetDate) {
    console.log(`🚴 [DEBUG] scrollToDateColumn called:`, {
      targetDate,
      allDateColumns: Array.from(document.querySelectorAll('[data-date]')).map(el => el.getAttribute('data-date'))
    })
    
    const dateColumn = document.querySelector(`[data-date="${targetDate}"]`)
    console.log(`🚴 [DEBUG] Date column lookup result:`, {
      targetDate,
      found: !!dateColumn,
      dateColumn: dateColumn ? dateColumn.outerHTML.substring(0, 100) + '...' : null
    })
    
    if (dateColumn) {
      console.log(`🚴 [DEBUG] Scrolling to date column ${targetDate}`)
      dateColumn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    } else {
      console.error(`🚴 [DEBUG] Date column not found for ${targetDate}`)
    }
  }

  /**
   * Switch to a tab
   * @param {Event} event - Click event from tab button
   */
  switchTab(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab

    if (tab === this.activeTabValue) return

    console.log(`🚴 BikeHunterTabs: Switching to ${tab}`)
    this.activeTabValue = tab

    // Save to sessionStorage
    sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, tab)

    this.updateTabStyles()
    this.updatePanelVisibility()

    // Lazy load map when Ruta or Advertencias tab is first selected (we need route data for warnings too)
    if ((tab === "ruta" || tab === "advertencias") && !this.mapLoadedValue) {
      this.loadMap()
    }
    
    // If mapLoaded but cache was invalidated (en_ruta changed), force refresh
    if ((tab === "ruta" || tab === "advertencias") && this.mapLoadedValue && !this.cachedRouteData) {
      console.log(`🚴 BikeHunterTabs: Cache invalidated, reloading map`)
      this.loadMap()
    }
  }

  /**
   * Update tab button styles based on active tab
   */
  updateTabStyles() {
    this.tabTargets.forEach(tabEl => {
      const isActive = tabEl.dataset.tab === this.activeTabValue

      // Remove all state classes first
      tabEl.classList.remove(
        "bg-blue-50", "text-blue-800", "ring-1", "ring-blue-400",
        "text-gray-700", "hover:bg-gray-50"
      )

      // Add appropriate classes
      if (isActive) {
        tabEl.classList.add("bg-blue-50", "text-blue-800", "ring-1", "ring-blue-400")
      } else {
        tabEl.classList.add("text-gray-700", "hover:bg-gray-50")
      }
    })
  }

  /**
   * Toggle panel visibility
   */
  updatePanelVisibility() {
    if (this.hasCronogramaPanelTarget) {
      this.cronogramaPanelTarget.classList.toggle("hidden", this.activeTabValue !== "cronograma")
    }
    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.classList.toggle("hidden", this.activeTabValue !== "ruta")
    }
    if (this.hasAdvertenciasPanelTarget) {
      this.advertenciasPanelTarget.classList.toggle("hidden", this.activeTabValue !== "advertencias")
    }
    
    // Block body scroll on mobile when ruta tab is active (for full-screen map)
    this.updateBodyScrollLock()
  }
  
  /**
   * Block/unblock body scroll on mobile when ruta panel is active
   */
  updateBodyScrollLock() {
    const isMobile = window.innerWidth < 768 // md breakpoint
    const isRutaActive = this.activeTabValue === "ruta"
    
    if (isMobile && isRutaActive) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }

  /**
   * Load Google Maps and route data
   */
  async loadMap() {
    try {
      console.log("🗺️ BikeHunterTabs: Loading map...")

      // Show loading indicator
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.remove("hidden")
      }
      if (this.hasMapContainerTarget) {
        this.mapContainerTarget.classList.add("hidden")
      }

      // Load Google Maps API if not present
      await this.ensureGoogleMapsLoaded()

      // Fetch route data (use cache if available)
      const routeData = await this.fetchRouteData()

      // Always update warnings panel
      this.updateWarningsPanel(routeData)

      if (routeData.success) {
        // Initialize map
        this.initializeMap(routeData)
        this.mapLoadedValue = true
        console.log("🗺️ BikeHunterTabs: Map loaded successfully")
      } else {
        // Show error but we already updated warnings
        throw new Error(routeData.error || 'Error loading route')
      }

    } catch (error) {
      console.error("❌ BikeHunterTabs: Error loading map:", error)
      this.showMapError(error.message)
    }
  }

  /**
   * Ensure Google Maps API is loaded
   */
  async ensureGoogleMapsLoaded() {
    if (window.google && window.google.maps) {
      return Promise.resolve()
    }

    const apiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content
    if (!apiKey) {
      throw new Error('Google Maps API key not found')
    }

    return new Promise((resolve, reject) => {
      // Set up error callback for Google Maps auth errors
      window.gm_authFailure = () => {
        console.error('🗺️ Google Maps auth failure - check API key restrictions in Google Cloud Console')
        reject(new Error('Google Maps authentication failed. The API key may have referrer restrictions that block PWA access.'))
      }

      const script = document.createElement('script')
      // Added 'marker' library for AdvancedMarkerElement support
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,marker&callback=Function.prototype`
      script.async = true
      script.defer = true
      script.onload = () => {
        // Small delay to allow Google Maps to initialize
        setTimeout(resolve, 100)
      }
      script.onerror = () => reject(new Error('Failed to load Google Maps script'))
      document.head.appendChild(script)
    })
  }

  /**
   * Fetch route data from server (with caching)
   */
  async fetchRouteData() {
    // Return cached data if available
    if (this.cachedRouteData) {
      console.log("🗺️ BikeHunterTabs: Using cached route data")
      return this.cachedRouteData
    }

    // Deduplicate in-flight requests
    if (this.fetchPromise) {
      console.log("🗺️ BikeHunterTabs: Waiting for in-flight route fetch...")
      return this.fetchPromise
    }

    this.fetchPromise = (async () => {
      try {
        const response = await fetch(`/apps/home-repairs/tickets/daily_route_data?date=${this.dateValue}`, {
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          // Do not throw on HTTP errors, return a structured error response
          // This allows the caller to handle the error gracefully, e.g., show warnings
          const errorData = await response.json().catch(() => ({ error: `HTTP error: ${response.status}` }))
          return { success: false, error: errorData.error || `HTTP error: ${response.status}`, warnings: errorData.warnings || [] }
        }

        const data = await response.json()

        // Cache the data
        this.cachedRouteData = data
        return data
      } finally {
        this.fetchPromise = null
      }
    })()

    return this.fetchPromise
  }

  /**
   * Check for warnings without initializing the map
   */
  async checkForWarnings() {
    try {
      // Fetch route data (will be cached for when map is needed)
      // This populates the warnings/error badge
      const data = await this.fetchRouteData()

      this.updateWarningsPanel(data)

    } catch (error) {
      console.warn("⚠️ BikeHunterTabs: Could not check for warnings:", error)
    }
  }

  /**
   * Initialize Google Map with route data
   */
  async initializeMap(data) {
    console.log('🗺️ initializeMap: Starting...')
    console.log('🗺️ initializeMap: Data received:', {
      routeCount: data.routes?.length,
      recommendedCount: data.recommended_tickets?.length,
      notInRouteCount: data.not_in_route_tickets?.length
    })

    if (!this.hasMapContainerTarget) {
      console.error('🗺️ initializeMap: Map container target not found!')
      throw new Error('Map container not found')
    }

    // Hide loading, show map
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
    this.mapContainerTarget.classList.remove("hidden")

    // Always use legacy markers for reliability
    this.useAdvancedMarkers = false

    // Determine map center from first route's origin, fallback origin, or first ticket
    const firstRoute = data.routes && data.routes.length > 0 ? data.routes[0] : null
    let mapCenter = firstRoute?.origin || data.origin
    if (!mapCenter) {
      const fallbackTickets = data.not_in_route_tickets || []
      if (fallbackTickets.length > 0 && fallbackTickets[0].coordinates) {
        mapCenter = fallbackTickets[0].coordinates
      }
    }
    if (!mapCenter) {
      mapCenter = { lat: 40.4168, lng: -3.7038 } // Madrid fallback
    }

    const mapOptions = {
      zoom: 13,
      center: mapCenter,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      clickableIcons: false
    }

    this.map = new google.maps.Map(this.mapContainerTarget, mapOptions)

    // Add Bicycling Layer to show bike lanes
    const bikeLayer = new google.maps.BicyclingLayer()
    bikeLayer.setMap(this.map)

    // Close InfoWindow when clicking on the map
    this.map.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.close()
        this.currentInfoWindow = null
      }
    })

    // Clear previous multi-mechanic state
    this.mechanicRoutes.forEach(rs => {
      if (rs.polyline) rs.polyline.setMap(null)
      rs.markers.forEach(m => m.setMap(null))
      if (rs.originMarker) rs.originMarker.setMap(null)
    })
    this.mechanicRoutes.clear()
    this.markers = []

    // Process multi-mechanic routes
    if (data.routes && data.routes.length > 0) {
      data.routes.forEach(route => {
        const mechInfo = {
          userId: route.user_id,
          userName: route.user_nombre_corto,
          userColor: route.user_color,
          isOwn: route.is_own_route
        }

        const routeState = {
          ...mechInfo,
          polyline: null,
          markers: [],
          originMarker: null,
          visible: true
        }

        // Add origin marker for this mechanic
        if (route.origin) {
          routeState.originMarker = this.addOriginMarkerLegacy(route.origin, mechInfo)
        }

        // Add ticket markers for this mechanic's route
        route.tickets.forEach((ticket, index) => {
          const marker = this.addTicketMarkerLegacy(ticket, index, mechInfo)
          routeState.markers.push(marker)
        })

        // Draw polyline with mechanic's color
        if (route.polyline) {
          routeState.polyline = this.drawMechanicRoute(route)
        }

        this.mechanicRoutes.set(route.user_id, routeState)
        console.log(`🗺️ initializeMap: Route for ${route.user_nombre_corto} - ${route.tickets.length} stops`)
      })
    } else if (data.origin) {
      // no_route case: show origin marker without mechanic context
      this.addOriginMarkerLegacy(data.origin)
    }

    // Add not-in-route ticket markers (gray pins)
    if (data.not_in_route_tickets && data.not_in_route_tickets.length > 0) {
      console.log('🗺️ initializeMap: Adding', data.not_in_route_tickets.length, 'not-in-route ticket markers')
      data.not_in_route_tickets.forEach((ticket) => {
        this.addNotInRouteTicketMarker(ticket)
      })
    }

    // Add recommended ticket markers (green pins)
    if (data.recommended_tickets && data.recommended_tickets.length > 0) {
      console.log('🗺️ initializeMap: Adding', data.recommended_tickets.length, 'recommended ticket markers')
      data.recommended_tickets.forEach((ticket) => {
        this.addRecommendedTicketMarker(ticket)
      })
    }

    // Add layer control panel if multiple mechanics have routes
    if (this.mechanicRoutes.size > 1) {
      this.addLayerControl()
    }

    // Fit bounds to show all visible markers
    const bounds = new google.maps.LatLngBounds()
    let hasPoints = false

    this.mechanicRoutes.forEach(rs => {
      if (rs.originMarker) {
        const pos = rs.originMarker.getPosition()
        if (pos) { bounds.extend(pos); hasPoints = true }
      }
      rs.markers.forEach(m => {
        const pos = m.getPosition()
        if (pos) { bounds.extend(pos); hasPoints = true }
      })
    })

    const extraTickets = [...(data.not_in_route_tickets || []), ...(data.recommended_tickets || [])]
    extraTickets.forEach(t => {
      if (t.coordinates) { bounds.extend(t.coordinates); hasPoints = true }
    })

    // Include top-level origin for no_route case
    if (data.origin && !firstRoute) {
      bounds.extend(data.origin)
      hasPoints = true
    }

    if (hasPoints) {
      this.map.fitBounds(bounds)
    } else {
      this.map.setCenter(mapCenter)
      this.map.setZoom(13)
    }

    // Show route info
    this.showRouteInfo(data)
    console.log('🗺️ initializeMap: Complete! Mechanic routes:', this.mechanicRoutes.size, 'Extra markers:', this.markers.length)
  }

  /**
   * Add origin marker to map
   */
  addOriginMarker(origin) {
    console.log('🗺️ addOriginMarker: useAdvancedMarkers =', this.useAdvancedMarkers)
    
    if (this.useAdvancedMarkers) {
      console.log('🗺️ addOriginMarker: Creating AdvancedMarkerElement for origin')
      // Create HTML content using Tailwind classes (avoids CSP blocking)
      const content = document.createElement('div')
      content.innerHTML = '<div class="bg-gray-800 text-white px-3 py-1.5 rounded-md text-xs font-bold shadow-md border-2 border-white whitespace-nowrap">PANOT</div>'
      console.log('🗺️ addOriginMarker: Content element created:', content.firstElementChild)
      
      try {
        const marker = new this.AdvancedMarkerElement({
          position: origin,
          map: this.map,
          title: 'Panot - Sede',
          content: content.firstElementChild,
          zIndex: 1000
        })
        console.log('🗺️ addOriginMarker: AdvancedMarkerElement created:', marker)
        this.markers.push(marker)
      } catch (error) {
        console.error('🗺️ addOriginMarker: Error creating AdvancedMarkerElement:', error)
        console.log('🗺️ addOriginMarker: Falling back to legacy marker')
        this.useAdvancedMarkers = false
        this.addOriginMarkerLegacy(origin)
      }
    } else {
      console.log('🗺️ addOriginMarker: Using legacy Marker')
      this.addOriginMarkerLegacy(origin)
    }
  }
  
  /**
   * Add origin marker using legacy Marker API
   * @param {Object} origin - {lat, lng}
   * @param {Object} mechInfo - {userId, userName, userColor, isOwn} or null for default
   * @returns {google.maps.Marker}
   */
  addOriginMarkerLegacy(origin, mechInfo = null) {
    const color = mechInfo?.userColor || '#1F2937'
    const label = mechInfo ? `SEDE · ${mechInfo.userName}` : 'PANOT'
    const labelWidth = Math.max(50, label.length * 6.5 + 20)
    const halfWidth = Math.round(labelWidth / 2)
    console.log('🗺️ addOriginMarkerLegacy: Creating marker at:', origin, 'mechanic:', mechInfo?.userName || 'default')

    const markerIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${labelWidth}" height="50" viewBox="0 0 ${labelWidth} 50">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.3"/>
            </filter>
          </defs>
          <g filter="url(#shadow)">
            <rect x="2" y="2" width="${labelWidth - 4}" height="20" rx="4" fill="${color}" stroke="white" stroke-width="1"/>
            <text x="${halfWidth}" y="15" text-anchor="middle" fill="white" font-family="sans-serif" font-size="8" font-weight="bold">${label}</text>
            <polygon points="${halfWidth},22 ${halfWidth - 4},27 ${halfWidth + 4},27" fill="${color}"/>
          </g>
        </svg>
      `),
      scaledSize: new google.maps.Size(labelWidth, 50),
      anchor: new google.maps.Point(halfWidth, 50)
    }

    const title = mechInfo ? `Sede - ${mechInfo.userName}` : 'Panot - Sede'
    const marker = new google.maps.Marker({
      position: origin,
      map: this.map,
      title: title,
      icon: markerIcon,
      zIndex: 1000
    })

    // Only push to shared markers array if not part of a mechanic route
    if (!mechInfo) this.markers.push(marker)
    return marker
  }

  /**
   * Add ticket marker to map
   */
  addTicketMarker(ticket, index) {
    console.log(`🗺️ addTicketMarker[${index}]: Adding marker for ticket`, ticket.id, 'useAdvanced:', this.useAdvancedMarkers)
    const hasWarning = ticket.warning
    
    if (this.useAdvancedMarkers) {
      console.log(`🗺️ addTicketMarker[${index}]: Creating AdvancedMarkerElement`)
      // Create card-style HTML content using Tailwind classes (avoids CSP blocking)
      const headerBg = hasWarning ? 'bg-red-500' : 'bg-blue-500'
      const borderColor = hasWarning ? 'border-red-500' : 'border-blue-500'
      
      const content = document.createElement('div')
      content.innerHTML = `
        <div class="bg-white rounded-lg shadow-lg min-w-[140px] max-w-[200px] overflow-hidden border-2 ${borderColor} cursor-pointer">
          <div class="${headerBg} text-white px-2 py-1 text-xs font-bold flex items-center justify-between">
            <span>#${index + 1}</span>
            ${hasWarning ? '<span class="bg-amber-400 text-amber-900 text-[9px] px-1 rounded font-bold">Ciudad incorrecta</span>' : ''}
          </div>
          <div class="p-1.5">
            <p class="text-[10px] font-semibold text-gray-800 m-0 leading-tight line-clamp-2">${this.truncateText(ticket.subject, 25)}</p>
            <p class="text-[9px] text-gray-500 mt-0.5 truncate">${ticket.nombre || 'Sin nombre'}</p>
            <p class="text-[9px] text-gray-400 mt-0.5 truncate">${this.truncateText(ticket.direccion, 28)}</p>
          </div>
        </div>
      `
      
      console.log(`🗺️ addTicketMarker[${index}]: Content created, creating AdvancedMarkerElement...`)
      try {
        const marker = new this.AdvancedMarkerElement({
          position: ticket.coordinates,
          map: this.map,
          title: `#${index + 1} - ${ticket.subject}`,
          content: content.firstElementChild,
          zIndex: 100 + index
        })
        console.log(`🗺️ addTicketMarker[${index}]: AdvancedMarkerElement created successfully:`, marker)
        
        marker.addListener('click', () => {
          this.showTicketInfoWindow(ticket, index, marker)
        })
        
        this.markers.push(marker)
      } catch (error) {
        console.error(`🗺️ addTicketMarker[${index}]: Error creating AdvancedMarkerElement:`, error)
        console.log(`🗺️ addTicketMarker[${index}]: Falling back to legacy marker`)
        this.addTicketMarkerLegacy(ticket, index)
      }
    } else {
      console.log(`🗺️ addTicketMarker[${index}]: Using legacy Marker`)
      this.addTicketMarkerLegacy(ticket, index)
    }
  }
  
  /**
   * Add ticket marker using legacy Marker API
   * @param {Object} ticket - Ticket data with coordinates
   * @param {Number} index - Position in route
   * @param {Object} mechInfo - {userId, userName, userColor, isOwn} or null
   * @returns {google.maps.Marker}
   */
  addTicketMarkerLegacy(ticket, index, mechInfo = null) {
    const hasWarning = ticket.warning
    const defaultColor = mechInfo?.userColor || '#F97316'
    const bgColor = hasWarning ? '#EF4444' : defaultColor
    
    // All pins use the same size (no halo, just color difference)
    const width = 32
    const height = 42
    const anchorX = 16
    const anchorY = 42

    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
          <filter id="shadow${index}" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.3"/>
          </filter>
        </defs>
        <g filter="url(#shadow${index})">
          <path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 26 16 26s16-14 16-26c0-8.836-7.164-16-16-16z" fill="${bgColor}"/>
          <circle cx="16" cy="16" r="10" fill="white"/>
          <text x="16" y="20" text-anchor="middle" fill="${bgColor}" font-family="sans-serif" font-size="12" font-weight="bold">${index + 1}</text>
        </g>
      </svg>
    `

    const markerIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgContent),
      scaledSize: new google.maps.Size(width, height),
      anchor: new google.maps.Point(anchorX, anchorY)
    }

    const marker = new google.maps.Marker({
      position: ticket.coordinates,
      map: this.map,
      title: `#${index + 1} - ${ticket.subject}`,
      icon: markerIcon,
      zIndex: 100 + index
    })

    marker.addListener('click', () => {
      this.showTicketInfoWindow(ticket, index, marker, mechInfo)
    })

    // Only push to shared markers array if not part of a mechanic route
    if (!mechInfo) this.markers.push(marker)
    return marker
  }
  
  /**
   * Truncate text with ellipsis
   */
  truncateText(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }
  
  /**
   * Show InfoWindow with minimal ticket summary
   */
  showTicketInfoWindow(ticket, index, marker, mechInfo = null) {
    // Use direccion_formateada if available, fallback to direccion
    const displayAddress = ticket.direccion_formateada || ticket.direccion || 'Dirección no disponible'
    const customerName = ticket.nombre || 'Sin nombre'
    const isReadOnly = mechInfo && !mechInfo.isOwn

    // Mechanic badge (shown when multi-mechanic)
    const mechanicBadge = mechInfo
      ? `<span class="gm-infowindow-mechanic-badge" style="background-color: ${mechInfo.userColor}; color: white; font-size: 9px; padding: 1px 5px; border-radius: 3px; margin-left: 4px;">${mechInfo.userName}</span>`
      : ''

    // Actions: full controls for own route, view-only for others
    const actionsHtml = isReadOnly
      ? `<div class="gm-infowindow-actions">
            <div class="gm-infowindow-button-group">
              <button type="button" 
                      class="gm-infowindow-btn gm-infowindow-btn-primary"
                      onclick="window.dispatchEvent(new CustomEvent('view-ticket-from-map', { detail: { ticketId: ${ticket.id} } }))">
                Ver ticket
              </button>
            </div>
          </div>`
      : `<div class="gm-infowindow-actions">
            <div class="gm-infowindow-button-group">
              <button type="button" 
                      class="gm-infowindow-resolve-btn"
                      data-ticket-id="${ticket.id}">
                Resolver
              </button>
              <button type="button" 
                      class="gm-infowindow-toggle"
                      role="switch"
                      aria-checked="true"
                      data-ticket-id="${ticket.id}"
                      onclick="window.dispatchEvent(new CustomEvent('toggle-en-ruta-from-map', { detail: { ticketId: ${ticket.id} } }))">
                <span class="sr-only">En ruta</span>
                <span class="gm-infowindow-toggle-indicator"></span>
              </button>
            </div>
          </div>`

    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="gm-infowindow-content">
          <div class="gm-infowindow-header-compact">
            <span class="gm-infowindow-number">#${index + 1}</span>
            <span class="gm-infowindow-subject">${this.truncateText(ticket.subject, 28)}</span>
            ${mechanicBadge}
          </div>
          <p class="gm-infowindow-cliente">${this.truncateText(customerName, 30)}</p>
          <p class="gm-infowindow-address">${this.truncateText(displayAddress, 40)}</p>
          ${actionsHtml}
        </div>
      `,
      maxWidth: 260
    })
    
    // Close any existing InfoWindow
    if (this.currentInfoWindow) {
      this.currentInfoWindow.close()
    }
    
    infoWindow.open(this.map, marker)
    this.currentInfoWindow = infoWindow
    
    // Add click listener to the Resolve button after InfoWindow DOM is ready
    google.maps.event.addListener(infoWindow, 'domready', () => {
      const resolveBtn = document.querySelector('.gm-infowindow-resolve-btn')
      console.log('🚴 InfoWindow domready: resolve button lookup', {
        found: !!resolveBtn,
        ticketId: ticket.id,
        button: resolveBtn
      })
      if (resolveBtn) {
        resolveBtn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          const ticketId = parseInt(resolveBtn.dataset.ticketId)
          console.log('🚴 InfoWindow Resolve button clicked:', {
            ticketId,
            fechaProgramada: ticket.fecha_programada,
            ticket: ticket
          })
          window.dispatchEvent(new CustomEvent('resolve-ticket-from-map', { 
            detail: { ticketId: ticketId, fechaProgramada: ticket.fecha_programada } 
          }))
        })
      }
    })
    
    // Center map with smooth animation and offset to show InfoWindow
    this.panToInfoWindow(ticket.coordinates)
  }
  
  /**
   * Pan map to center InfoWindow with smooth animation
   */
  panToInfoWindow(coordinates) {
    // Use panTo for smooth animation
    const latLng = new google.maps.LatLng(coordinates)
    
    // Wait for projection to be available
    const projection = this.map.getProjection()
    if (projection) {
      const zoom = this.map.getZoom()
      const point = projection.fromLatLngToPoint(latLng)
      
      // Offset strategy: InfoWindow appears ABOVE the pin
      // So we need the pin to be BELOW center and SLIGHTLY RIGHT
      // This leaves space above for the InfoWindow to be visible
      // Y: +120px (move center South -> pin appears lower, InfoWindow has space above)
      // X: +40px (move center East -> pin slightly to the left, InfoWindow more centered)
      const offsetPixelsY = 120
      const offsetPixelsX = 170
      
      const offsetWorldY = offsetPixelsY / Math.pow(2, zoom)
      const offsetWorldX = offsetPixelsX / Math.pow(2, zoom)
      
      const newPoint = new google.maps.Point(
        point.x + offsetWorldX,
        point.y + offsetWorldY
      )
      
      const newCenter = projection.fromPointToLatLng(newPoint)
      this.map.panTo(newCenter)
    } else {
      // Fallback: simple panTo if projection not ready
      this.map.panTo(latLng)
    }
  }
  
  /**
   * Add recommended ticket marker to map (green, not in route)
   */
  addRecommendedTicketMarker(ticket) {
    console.log('🗺️ addRecommendedTicketMarker: Adding for ticket', ticket.id, 'useAdvanced:', this.useAdvancedMarkers)
    
    if (this.useAdvancedMarkers) {
      console.log('🗺️ addRecommendedTicketMarker: Creating AdvancedMarkerElement')
      // Create card-style HTML content using Tailwind classes (avoids CSP blocking)
      const content = document.createElement('div')
      content.innerHTML = `
        <div class="bg-white rounded-lg shadow-lg min-w-[130px] max-w-[180px] overflow-hidden border-2 border-green-500 opacity-95 cursor-pointer">
          <div class="bg-green-500 text-white px-2 py-1 text-[10px] font-bold flex items-center justify-between">
            <span>Recomendado</span>
            <span class="bg-green-200 text-green-800 text-[9px] px-1 rounded font-bold">${ticket.distance_km}km</span>
          </div>
          <div class="p-1.5">
            <p class="text-[10px] font-semibold text-green-800 m-0 leading-tight line-clamp-2">${this.truncateText(ticket.subject, 22)}</p>
            <p class="text-[9px] text-gray-500 mt-0.5 truncate">${this.truncateText(ticket.direccion, 25)}</p>
            <p class="text-[8px] text-green-500 mt-1 pt-1 border-t border-green-200 text-center italic">Cerca de tu ruta</p>
          </div>
        </div>
      `
      
      try {
        const marker = new this.AdvancedMarkerElement({
          position: ticket.coordinates,
          map: this.map,
          title: `Recomendado: ${ticket.subject} (${ticket.distance_km}km)`,
          content: content.firstElementChild,
          zIndex: 50
        })
        console.log('🗺️ addRecommendedTicketMarker: AdvancedMarkerElement created:', marker)
        
        marker.addListener('click', () => {
          this.showRecommendedTicketInfoWindow(ticket, marker)
        })
        
        this.markers.push(marker)
      } catch (error) {
        console.error('🗺️ addRecommendedTicketMarker: Error creating AdvancedMarkerElement:', error)
        this.addRecommendedTicketMarkerLegacy(ticket)
      }
    } else {
      console.log('🗺️ addRecommendedTicketMarker: Using legacy Marker')
      this.addRecommendedTicketMarkerLegacy(ticket)
    }
  }
  
  /**
   * Add recommended ticket marker using legacy Marker API
   */
  addRecommendedTicketMarkerLegacy(ticket) {
    const markerIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.3"/>
            </filter>
          </defs>
          <g filter="url(#shadow)">
            <path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 26 16 26s16-14 16-26c0-8.836-7.164-16-16-16z" fill="#22C55E"/>
            <circle cx="16" cy="16" r="10" fill="white"/>
            <text x="16" y="13" text-anchor="middle" fill="#22C55E" font-family="sans-serif" font-size="7" font-weight="bold">${ticket.distance_km}</text>
            <text x="16" y="21" text-anchor="middle" fill="#22C55E" font-family="sans-serif" font-size="6">km</text>
          </g>
        </svg>
      `),
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42)
    }

    const marker = new google.maps.Marker({
      position: ticket.coordinates,
      map: this.map,
      title: `Recomendado: ${ticket.subject} (${ticket.distance_km}km)`,
      icon: markerIcon,
      zIndex: 50,
      opacity: 0.9
    })

    marker.addListener('click', () => {
      this.showRecommendedTicketInfoWindow(ticket, marker)
    })

    this.markers.push(marker)
  }
  
  /**
   * Add not-in-route ticket marker (gray pin - can be added to route)
   */
  addNotInRouteTicketMarker(ticket) {
    // Internal = gris (#9CA3AF), Asistencia a domicilio = amarillo/naranja (#F59E0B)
    const isInternal = ticket.pipeline === 'Internal'
    const color = isInternal ? '#9CA3AF' : '#F59E0B'
    const colorName = isInternal ? 'gray' : 'amber'
    
    console.log(`🗺️ addNotInRouteTicketMarker: Adding ${colorName} marker for ticket`, ticket.id, `(${ticket.pipeline})`)
    
    const markerIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.3"/>
            </filter>
          </defs>
          <g filter="url(#shadow)">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 23 14 23s14-12.5 14-23c0-7.732-6.268-14-14-14z" fill="${color}"/>
            <circle cx="14" cy="14" r="8" fill="white"/>
            <circle cx="14" cy="14" r="4" fill="${color}"/>
          </g>
        </svg>
      `),
      scaledSize: new google.maps.Size(28, 38),
      anchor: new google.maps.Point(14, 38)
    }

    const marker = new google.maps.Marker({
      position: ticket.coordinates,
      map: this.map,
      title: `${ticket.subject} (No en ruta)`,
      icon: markerIcon,
      zIndex: 30, // Lower than in-route tickets
      opacity: 0.8
    })

    marker.addListener('click', () => {
      this.showNotInRouteTicketInfoWindow(ticket, marker)
    })

    this.markers.push(marker)
  }
  
  /**
   * Show InfoWindow for not-in-route ticket
   */
  showNotInRouteTicketInfoWindow(ticket, marker) {
    const displayAddress = ticket.direccion_formateada || ticket.direccion || 'Dirección no disponible'
    const customerName = ticket.nombre || 'Sin nombre'
    
    // Internal = gris, Asistencia a domicilio = amarillo/naranja
    const isInternal = ticket.pipeline === 'Internal'
    const badgeClass = isInternal ? 'gm-infowindow-badge-gray' : 'gm-infowindow-badge-amber'
    const badgeText = isInternal ? 'Internal' : 'A domicilio'
    const titleColor = isInternal ? '#6B7280' : '#D97706'

    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="gm-infowindow-content">
          <div class="gm-infowindow-header-compact">
            <span class="gm-infowindow-badge ${badgeClass}">${badgeText}</span>
          </div>
          <h3 class="gm-infowindow-title" style="color: ${titleColor};">${this.truncateText(ticket.subject, 28)}</h3>
          <p class="gm-infowindow-cliente">${this.truncateText(customerName, 30)}</p>
          <p class="gm-infowindow-address">${this.truncateText(displayAddress, 40)}</p>
          <div class="gm-infowindow-actions">
            <div class="gm-infowindow-button-group">
              <button type="button" 
                      class="gm-infowindow-btn gm-infowindow-btn-primary"
                      onclick="window.dispatchEvent(new CustomEvent('view-ticket-from-map', { detail: { ticketId: ${ticket.id} } }))">
                Ver ticket
              </button>
              <button type="button" 
                      class="gm-infowindow-toggle"
                      role="switch"
                      aria-checked="false"
                      data-ticket-id="${ticket.id}"
                      onclick="window.dispatchEvent(new CustomEvent('toggle-en-ruta-from-map', { detail: { ticketId: ${ticket.id} } }))">
                <span class="sr-only">En ruta</span>
                <span class="gm-infowindow-toggle-indicator"></span>
              </button>
            </div>
          </div>
        </div>
      `,
      maxWidth: 240
    })

    if (this.currentInfoWindow) {
      this.currentInfoWindow.close()
    }
    infoWindow.open(this.map, marker)
    this.currentInfoWindow = infoWindow
  }
  
  /**
   * Show InfoWindow for recommended ticket
   */
  showRecommendedTicketInfoWindow(ticket, marker) {
    const fechaInfo = ticket.fecha_programada 
      ? `<p class="gm-infowindow-detail" style="color: #6B7280;">Programado: ${ticket.fecha_programada}</p>`
      : '<p class="gm-infowindow-detail" style="color: #6B7280; font-style: italic;">Sin fecha asignada</p>'

    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="gm-infowindow-content">
          <div class="gm-infowindow-header">
            <span class="gm-infowindow-badge">
              A ${ticket.distance_km}km de tu ruta
            </span>
          </div>
          <h3 class="gm-infowindow-title" style="color: #166534;">${ticket.subject}</h3>
          <p class="gm-infowindow-address">${ticket.direccion}</p>
          ${ticket.direccion_formateada ? `<p class="gm-infowindow-address-formatted">→ ${ticket.direccion_formateada}</p>` : ''}
          <p class="gm-infowindow-detail"><strong>Cliente:</strong> ${ticket.nombre || 'N/A'}</p>
          ${ticket.telefono ? `<p class="gm-infowindow-detail"><strong>Tel:</strong> <a href="tel:${ticket.telefono}" class="gm-infowindow-link">${ticket.telefono}</a></p>` : ''}
          ${fechaInfo}
          <div class="gm-infowindow-actions">
            <div class="gm-infowindow-button-group">
              <button type="button" 
                      class="gm-infowindow-btn gm-infowindow-btn-primary px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
                      onclick="window.dispatchEvent(new CustomEvent('view-ticket-from-map', { detail: { ticketId: ${ticket.id} } }))">
                Ver ticket
              </button>
              <button type="button" 
                      class="gm-infowindow-toggle relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border border-gray-300 transition-colors duration-200 ease-in-out focus:outline-none bg-gray-200"
                      role="switch"
                      aria-checked="false"
                      data-ticket-id="${ticket.id}"
                      onclick="window.dispatchEvent(new CustomEvent('toggle-en-ruta-from-map', { detail: { ticketId: ${ticket.id} } }))">
                <span class="sr-only">En ruta</span>
                <span class="gm-infowindow-toggle-indicator pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out translate-x-0"></span>
              </button>
            </div>
          </div>
        </div>
      `
    })
    
    if (this.currentInfoWindow) {
      this.currentInfoWindow.close()
    }
    infoWindow.open(this.map, marker)
    this.currentInfoWindow = infoWindow
  }
  
  /**
   * Format pipeline stage name for display
   */
  formatPipelineName(stage) {
    const names = {
      'programado': 'Programado',
      'en_reparacion': 'En reparación',
      'pendiente_respuesta': 'Pendiente',
      'resuelto': 'Resuelto'
    }
    return names[stage] || stage || 'Pendiente'
  }

  /**
   * Draw a single mechanic's route polyline on map
   * @param {Object} route - Route data with polyline, user_color, is_own_route
   * @returns {google.maps.Polyline}
   */
  drawMechanicRoute(route) {
    const path = google.maps.geometry.encoding.decodePath(route.polyline)

    const polyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: route.user_color || '#F97316',
      strokeOpacity: route.is_own_route ? 0.9 : 0.6,
      strokeWeight: route.is_own_route ? 5 : 3
    })
    polyline.setMap(this.map)
    return polyline
  }

  /**
   * Show route information overlay (multi-mechanic)
   */
  showRouteInfo(data) {
    if (this.hasRouteInfoTarget) {
      let warningsHtml = ''
      let noRouteMessageHtml = ''
      let routeStatsHtml = ''

      // Show message when there's no route calculated (only gray pins)
      if (data.no_route) {
        const notInRouteCount = data.not_in_route_tickets ? data.not_in_route_tickets.length : 0
        noRouteMessageHtml = `
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <p class="text-xs font-semibold text-amber-700 mb-1">Sin ruta calculada</p>
            <p class="text-xs text-amber-600">${notInRouteCount} ticket(s) abierto(s) disponibles.</p>
            <p class="text-xs text-amber-600 mt-1">Activa "En ruta" en los tickets para calcular la ruta óptima.</p>
          </div>
        `
      } else if (data.routes && data.routes.length > 0) {
        // Per-mechanic route stats
        routeStatsHtml = data.routes.map(route => `
          <div class="mb-3 pb-2 border-b border-gray-100 last:border-0">
            <div class="flex items-center gap-1.5 mb-1">
              <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" style="background-color: ${route.user_color}"></span>
              <span class="font-bold text-sm">${route.user_nombre_corto}</span>
            </div>
            <p class="text-xs text-gray-600 mb-0.5">
              <strong>Distancia:</strong> ${(route.distance_meters / 1000).toFixed(1)} km
            </p>
            <p class="text-xs text-gray-600 mb-0.5">
              <strong>Duración:</strong> ${Math.round(route.duration_seconds / 60)} min
            </p>
            <p class="text-xs text-gray-600">
              <strong>Paradas:</strong> ${route.tickets.length}
            </p>
          </div>
        `).join('')
      }

      // Show warnings if any
      if (data.warnings && data.warnings.length > 0) {
        warningsHtml = `
          <div class="mt-3 pt-3 border-t border-orange-200">
            <p class="text-xs font-semibold text-orange-700 mb-2">⚠️ Advertencias (${data.warnings.length})</p>
            ${data.warnings.map(w => {
              const isPendingInvoice = w.type === 'pending_invoices'
              const bgColor = isPendingInvoice ? 'bg-red-50' : 'bg-orange-50'
              const textColor = isPendingInvoice ? 'text-red-600' : 'text-orange-600'
              const icon = isPendingInvoice ? '💳' : '⚠️'
              
              return `
                <div class="text-xs ${bgColor} p-2 rounded mb-1">
                  <span class="font-medium">${icon} ${w.ticket_subject || 'Ticket #' + w.ticket_id}</span>
                  <p class="${textColor}">${w.message}</p>
                </div>
              `
            }).join('')}
          </div>
        `
      }

      this.routeInfoTarget.innerHTML = `
        <button type="button" 
                class="absolute top-1 right-1 text-gray-400 hover:text-gray-600 p-1"
                data-action="click->bike-hunter-tabs#toggleRouteInfo"
                title="Ocultar panel">
          ✕
        </button>
        ${noRouteMessageHtml}
        ${routeStatsHtml ? `<h3 class="font-bold text-sm mb-2">${data.routes.length > 1 ? 'Rutas en Bicicleta' : 'Ruta en Bicicleta'}</h3>${routeStatsHtml}` : ''}
        ${warningsHtml}
      `

      // Store warning count for the toggle button
      this.warningCount = data.warnings ? data.warnings.length : 0

      // Store data for Google Maps button
      this.currentRouteData = data

      // Keep panel hidden by default, show toggle button
      this.routeInfoTarget.classList.add("hidden")
      this.updateRouteInfoToggleButton(true)
      this.updateGoogleMapsButton(true)
    }
  }

  /**
   * Toggle route info panel visibility
   */
  toggleRouteInfo() {
    const isHidden = this.routeInfoTarget.classList.toggle("hidden")
    this.updateRouteInfoToggleButton(isHidden)
  }

  /**
   * Update the floating toggle button visibility
   */
  updateRouteInfoToggleButton(showButton) {
    // Find or create the toggle button
    let toggleBtn = this.element.querySelector('[data-route-info-toggle]')

    if (showButton) {
      if (!toggleBtn) {
        toggleBtn = document.createElement('button')
        toggleBtn.type = 'button'
        toggleBtn.dataset.routeInfoToggle = 'true'
        toggleBtn.style.cssText = `
          background: none center / 24px no-repeat rgb(255, 255, 255);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 0;
          margin: 0;
          padding: 0;
          position: absolute;
          bottom: 24px;
          left: 10px;
          cursor: pointer;
          user-select: none;
          width: 40px;
          height: 40px;
          border-radius: 2px;
          box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
          z-index: 10;
        `
        toggleBtn.title = 'Mostrar info de ruta'
        toggleBtn.dataset.action = 'click->bike-hunter-tabs#toggleRouteInfo'

        // Insert after mapContainer
        if (this.hasMapContainerTarget) {
          this.mapContainerTarget.parentElement.appendChild(toggleBtn)
        }
      }

      // Update button content with info icon and optional warning badge
      const warningBadge = this.warningCount > 0
        ? `<span class="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-4 h-4 rounded-full flex items-center justify-center px-1">${this.warningCount}</span>`
        : ''

      toggleBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        ${warningBadge}
      `

      toggleBtn.classList.remove('hidden')
    } else if (toggleBtn) {
      toggleBtn.classList.add('hidden')
    }
  }

  /**
   * Update the floating Google Maps button visibility
   */
  updateGoogleMapsButton(showButton) {
    // Find or create the Google Maps button
    let mapsBtn = this.element.querySelector('[data-google-maps-btn]')

    if (showButton && this.currentRouteData) {
      const mapsUrl = this.buildGoogleMapsUrl(this.currentRouteData)

      if (!mapsBtn) {
        mapsBtn = document.createElement('a')
        mapsBtn.dataset.googleMapsBtn = 'true'
        mapsBtn.href = mapsUrl
        mapsBtn.target = '_blank'
        mapsBtn.rel = 'noopener noreferrer'
        mapsBtn.style.cssText = `
          background: #4285F4;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: 0;
          margin: 0;
          padding: 8px 12px;
          position: absolute;
          bottom: 24px;
          left: 60px;
          cursor: pointer;
          user-select: none;
          border-radius: 2px;
          box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
          z-index: 10;
          text-decoration: none;
          color: white;
          font-size: 13px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        `
        mapsBtn.title = 'Abrir ruta en Google Maps'

        // Insert after mapContainer
        if (this.hasMapContainerTarget) {
          this.mapContainerTarget.parentElement.appendChild(mapsBtn)
        }
      } else {
        mapsBtn.href = mapsUrl
      }

      mapsBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
        </svg>
        <span>Google Maps</span>
      `

      mapsBtn.classList.remove('hidden')
    } else if (mapsBtn) {
      mapsBtn.classList.add('hidden')
    }
  }

  /**
   * Build Google Maps URL for navigation (uses own route or first route)
   */
  buildGoogleMapsUrl(routeOrData) {
    // Accept either a single route object or data with routes array
    let route = routeOrData
    if (routeOrData.routes) {
      route = routeOrData.routes.find(r => r.is_own_route) || routeOrData.routes[0]
    }
    if (!route || !route.origin || !route.tickets) return '#'

    const origin = `${route.origin.lat},${route.origin.lng}`
    const waypoints = route.tickets.map(t => `${t.coordinates.lat},${t.coordinates.lng}`).join('|')
    const destination = origin // Round trip

    // Use bicycling mode
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=bicycling`
  }

  /**
   * Show error message in map container
   */
  showMapError(message) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }

    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.innerHTML = `
        <div class="w-full h-[600px] flex items-center justify-center bg-gray-100 rounded-lg">
          <div class="text-center p-6 bg-red-50 rounded-lg">
            <p class="text-red-600 font-semibold mb-2">❌ Error al cargar ruta</p>
            <p class="text-red-500 text-sm">${message}</p>
            <button 
              type="button"
              class="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
              data-action="click->bike-hunter-tabs#retryLoadMap"
            >
            </button>
          </div>
        </div>
      `
    }
  }

  /**
   * Update the warnings panel with data
   */
  updateWarningsPanel(data) {
    if (!this.hasWarningsListTarget) return

    let warnings = [...(data.warnings || [])]

    // Include general error if present (e.g. route optimization failed)
    if (!data.success && data.error) {
      warnings.unshift({
        type: 'general_error',
        message: data.error,
        ticket_id: null,
        ticket_subject: 'Error General'
      })
    }
    const count = warnings.length

    // Update badge
    if (this.hasWarningCountBadgeTarget) {
      this.warningCountBadgeTarget.textContent = count
      if (count > 0) {
        this.warningCountBadgeTarget.classList.remove('hidden')
      } else {
        this.warningCountBadgeTarget.classList.add('hidden')
      }
    }

    if (count === 0) {
      this.warningsListTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-10 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-lg font-medium">¡Todo en orden!</p>
          <p class="text-sm">No hay advertencias ni errores en las direcciones.</p>
        </div>
      `
      return
    }

    // Render warnings
    this.warningsListTarget.innerHTML = warnings.map(w => {
      let actionButton = ''
      const isPendingInvoice = w.type === 'pending_invoices'

      // Determine field to edit
      const fieldToEdit = w.field || (w.type === 'geocode_failed' || w.type === 'city_mismatch' ? 'direccion_reparacion' : null)

      // If we can identify a field to edit, show the button
      if (fieldToEdit) {
        const buttonText = fieldToEdit === 'direccion_reparacion' ? 'Editar dirección' : 'Corregir campo'

        actionButton = `
          <button 
            type="button"
            onclick="window.dispatchEvent(new CustomEvent('edit-ticket-address', { detail: { ticketId: ${w.ticket_id}, field: '${fieldToEdit}' } }))"
            class="mt-3 inline-flex items-center px-4 py-2 border border-blue-600 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 cursor-pointer transition-colors">
            ${buttonText}
          </button>
        `
      }

      // Different colors for pending invoices
      const iconBgColor = isPendingInvoice ? 'bg-red-100' : 'bg-red-100'
      const iconColor = isPendingInvoice ? 'text-red-700' : 'text-red-600'
      const messageColor = isPendingInvoice ? 'text-red-700 font-semibold' : 'text-red-600 font-medium'
      const borderColor = isPendingInvoice ? 'border-red-200' : 'border-gray-200'
      const icon = isPendingInvoice ? '💳' : '⚠️'

      return `
        <div class="bg-white border ${borderColor} rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-start space-x-3">
            <div class="shrink-0 mt-0.5">
              <div class="${iconBgColor} rounded-full p-1">
                <span class="text-lg">${icon}</span>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900">
                ${w.ticket_subject || 'Ticket #' + w.ticket_id}
              </h3>
              <div class="mt-1 text-sm text-gray-600">
                <p class="${messageColor}">${w.message}</p>
                ${w.direccion_original ? `<p class="mt-1 text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100 font-mono break-all">${w.direccion_original}</p>` : ''}
              </div>
              ${actionButton}
            </div>
          </div>
        </div>
      `
    }).join('')
  }


  /**
   * Retry loading map after error
   */
  retryLoadMap() {
    this.cachedRouteData = null
    this.mapLoadedValue = false

    // Restore the original ruta panel HTML
    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.innerHTML = `
        <div class="w-full h-[600px] bg-gray-100 rounded-lg overflow-hidden relative">
          <div data-bike-hunter-tabs-target="loadingIndicator" class="absolute inset-0 flex items-center justify-center">
            <div class="text-center">
              <svg class="animate-spin h-10 w-10 text-orange-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-gray-600">Calculando ruta óptima...</p>
            </div>
          </div>
          <div data-bike-hunter-tabs-target="mapContainer" class="w-full h-full hidden"></div>
          <div data-bike-hunter-tabs-target="routeInfo" class="absolute top-4 left-4 bg-white p-4 rounded-lg shadow-lg z-10 hidden"></div>
        </div>
      `
    }

    this.loadMap()
  }

  /**
   * Add layer control panel for toggling mechanic route visibility
   * Only shown when there are multiple mechanics' routes on the map
   */
  addLayerControl() {
    // Remove existing layer control if any
    const existing = this.element.querySelector('[data-layer-control]')
    if (existing) existing.remove()

    const panel = document.createElement('div')
    panel.dataset.layerControl = 'true'
    panel.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: white;
      border-radius: 4px;
      box-shadow: rgba(0,0,0,0.3) 0px 1px 4px -1px;
      z-index: 10;
      padding: 8px 10px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 12px;
      max-height: 200px;
      overflow-y: auto;
    `

    let html = '<div style="font-weight: 600; margin-bottom: 6px; color: #374151;">Rutas</div>'

    this.mechanicRoutes.forEach((rs, userId) => {
      const checkedAttr = rs.visible ? 'checked' : ''
      html += `
        <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; padding: 2px 0; white-space: nowrap;">
          <input type="checkbox" ${checkedAttr} data-mechanic-layer-id="${userId}" 
                 style="accent-color: ${rs.userColor}; width: 14px; height: 14px; cursor: pointer;">
          <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${rs.userColor}; flex-shrink: 0;"></span>
          <span style="color: #1f2937;">${rs.userName}${rs.isOwn ? ' (tú)' : ''}</span>
          <span style="color: #9ca3af; font-size: 10px;">${rs.markers.length}</span>
        </label>
      `
    })

    panel.innerHTML = html

    // Attach change listeners
    panel.querySelectorAll('input[data-mechanic-layer-id]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const mechId = parseInt(e.target.dataset.mechanicLayerId)
        this.toggleMechanicVisibility(mechId, e.target.checked)
      })
    })

    // Insert in map container's parent
    if (this.hasMapContainerTarget) {
      this.mapContainerTarget.parentElement.appendChild(panel)
    }
  }

  /**
   * Toggle visibility of a mechanic's route (polyline + markers + origin)
   */
  toggleMechanicVisibility(userId, visible) {
    const rs = this.mechanicRoutes.get(userId)
    if (!rs) return

    rs.visible = visible

    if (rs.polyline) {
      rs.polyline.setMap(visible ? this.map : null)
    }
    rs.markers.forEach(m => {
      m.setMap(visible ? this.map : null)
    })
    if (rs.originMarker) {
      rs.originMarker.setMap(visible ? this.map : null)
    }

    console.log(`🗺️ toggleMechanicVisibility: ${rs.userName} → ${visible ? 'visible' : 'hidden'}`)
  }

  /**
   * Force refresh route data (can be called externally)
   */
  refreshRouteData() {
    this.cachedRouteData = null
    // Clear multi-mechanic state
    this.mechanicRoutes.forEach(rs => {
      if (rs.polyline) rs.polyline.setMap(null)
      rs.markers.forEach(m => m.setMap(null))
      if (rs.originMarker) rs.originMarker.setMap(null)
    })
    this.mechanicRoutes.clear()
    if (this.activeTabValue === "ruta") {
      this.loadMap()
    }
  }
}
